import { getRuleValue } from "../data/ruleRegistry";
// Physical building dimensions are owned by the DOMAIN layer (the bottom of the
// stack) and re-exported here under their ENGINE_* names. Do not redefine them:
// a local copy is how the domain's panel thickness drifted to 15 mm against the
// registry's 20 mm. See domain/geometry/buildingDimensions.ts.
import {
	CC_M,
	INTERIOR_BATTEN_D_M,
	INTERIOR_BATTEN_W_M,
	INTERIOR_LINING_D_M,
	JOIST_H_M,
	JOIST_W_M,
	PANEL_T_M,
	STUD_D_M,
	STUD_W_M,
	VAPOR_BARRIER_T_M,
	WALL_H_M,
} from "../domain/geometry/buildingDimensions";

export const ENGINE_CC_M = CC_M;
export const ENGINE_WALL_H_M = WALL_H_M;
export const ENGINE_JOIST_W_M = JOIST_W_M;
export const ENGINE_JOIST_H_M = JOIST_H_M;
export const ENGINE_STUD_W_M = STUD_W_M;
export const ENGINE_STUD_D_M = STUD_D_M;
export const ENGINE_PANEL_T_M = PANEL_T_M;

/**
 * Wall insulation batts stop this far short of the studs' inner face. The
 * wall pick/tint plane (`createInteractionOverlays`) sits in the middle of
 * that gap, so no rendered face is ever coplanar with it: a coplanar tint
 * z-fights into a checkerboard on the batts the moment a wall is selected.
 */
export const ENGINE_WALL_INSULATION_INNER_GAP_M = 0.002;
/** Wall pick/tint plane, measured inward from the wall's outer stud face. */
export const ENGINE_WALL_PICK_PLANE_DEPTH_M =
	ENGINE_STUD_D_M - ENGINE_WALL_INSULATION_INNER_GAP_M / 2;

// Exterior facade constants (AMA Hus)
export const ENGINE_CLADDING_T_M =
	getRuleValue<number>("exterior.claddingThicknessMm") / 1000;
export const ENGINE_CLADDING_COV_M =
	getRuleValue<number>("exterior.claddingCoverageMm") / 1000;
export const ENGINE_FURRING_D_M =
	getRuleValue<number>("exterior.furringDepthMm") / 1000;
export const ENGINE_FURRING_W_M =
	getRuleValue<number>("exterior.furringWidthMm") / 1000;
export const ENGINE_ROOF_PITCH_DEG = getRuleValue<number>(
	"exterior.roofPitchDegrees",
);
// Wind barrier membrane/board thickness in the ventilated facade. Shared by
// the 3D facade builder and both rectangular engines' BOM math so the
// rendered layer stack and the purchase list can never disagree.
export const ENGINE_WIND_BARRIER_T_M = 0.002;
// Lying spikläkt row spacing (c/c) in the korsläkt — shared by the renderer
// (wallStrips), the BOM quantity math, and the audit evidence rows.
export const ENGINE_FACADE_FURRING_CC_M = 0.6;
// Exposed face width of a knutbräda (corner board) — shared by the facade
// builder (board geometry) and the BOM quantity math (base-trim end offsets).
export const ENGINE_CORNER_BOARD_W_M = 0.09;
// ─── Interior wall layer stack (issue 114) ──────────────────
// The inside of a sauna wall is built regel → stenullsisolering in the bays →
// åldersbeständig bastufolie (ångspärr) → LIGGANDE glespanel/spikläkt →
// STÅENDE bastupanel. The guide copy locks that order, and these constants are
// the single source of truth shared by the 3D builder, the BOM quantity math
// and the audit evidence rows so they can never drift apart.
export const ENGINE_VAPOR_BARRIER_T_M = VAPOR_BARRIER_T_M;
export const ENGINE_INTERIOR_BATTEN_D_M = INTERIOR_BATTEN_D_M;
export const ENGINE_INTERIOR_BATTEN_W_M = INTERIOR_BATTEN_W_M;
/**
 * Row spacing (c/c) of the LYING glespanel. The bastupanel stands, so the
 * battens must lie across it — a standing batten under a standing board is
 * unnailable (see the korsläkt note for the facade equivalent).
 */
export const ENGINE_INTERIOR_BATTEN_CC_M = 0.6;
/**
 * Total interior build-out inward from the stud's inner face before the
 * bastupanel starts. Every room dimension (`innerW`/`innerL`, bench widths,
 * heater clearances, ceiling extents, drag bounds) measures through this.
 */
export const ENGINE_INTERIOR_LINING_D_M = INTERIOR_LINING_D_M;

// Base trim (nederkantsavslut) board height, and how far its top sits above
// the wall base. Their difference is the corner-board bottom extension, so the
// knutbrädor run down to the base trim's drip edge. Shared by the facade
// builder and the corner-trim BOM length (roof-aware corner heights).
export const ENGINE_BASE_TRIM_H_M = 0.095;
export const ENGINE_BASE_TRIM_TOP_ABOVE_BASE_M = 0.01;
